import React, { useCallback, useEffect, useState } from 'react';
import {
  FaClipboardList,
  FaExclamationTriangle,
  FaSearch,
  FaSpinner,
} from 'react-icons/fa';
import {
  fetchAdminFeedback,
  getCategoryClass,
  getCategoryLabel,
} from '../services/feedback';

const PAGE_SIZE = 10;

function parseDate(value) {
  if (!value) return null;
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    const date = new Date(year, month - 1, day, hour, minute, second);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTime(value) {
  const date = parseDate(value);
  if (!date) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getFeedbackUser(item) {
  return {
    name: item.userName || item.name || item.initiatedByName || '—',
    email: item.userEmail || item.email || item.initiatedByEmail || '',
    id: item.userId || item.initiatedByUserId,
  };
}

function getFeedbackMessage(item) {
  return item.message || item.details || item.body || '';
}

function getFeedbackSubject(item) {
  return item.subject || item.title || 'Feedback';
}

function getFeedbackCreatedAt(item) {
  return item.createdAt || item.created_at || item.submittedAt || item.updatedAt;
}

function getTotalPages(total, limit) {
  if (!total || total <= 0) return 1;
  return Math.max(1, Math.ceil(total / limit));
}

function getVisiblePages(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
  return [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
}

function AdminFeedbackPanel({ user, onTotalChange }) {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isSearchPending = searchInput.trim() !== search;

  const fetchFeedback = useCallback(async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');

    try {
      const { items: nextItems, pagination: nextPagination } = await fetchAdminFeedback({
        page,
        limit: PAGE_SIZE,
        search,
        token,
      });
      setItems(nextItems);
      setPagination(nextPagination);
      if (!search && typeof onTotalChange === 'function') {
        onTotalChange(nextPagination.total || 0);
      }
    } catch (err) {
      setItems([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0 });
      setError(err.message || 'Unable to load feedback.');
    } finally {
      setLoading(false);
    }
  }, [page, search, onTotalChange]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (user) {
      fetchFeedback();
    } else {
      setLoading(false);
      setItems([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0 });
    }
  }, [user, fetchFeedback]);

  const totalPages = getTotalPages(pagination.total, pagination.limit || PAGE_SIZE);
  const visiblePages = getVisiblePages(page, totalPages);
  const rangeStart = pagination.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = pagination.total === 0 ? 0 : Math.min(page * PAGE_SIZE, pagination.total);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  return (
    <>
      <div className="admin-search-bar">
        <label className="admin-search-field" htmlFor="admin-feedback-search">
          <FaSearch className="admin-search-icon" aria-hidden="true" />
          <input
            id="admin-feedback-search"
            type="text"
            className="admin-search-input"
            placeholder="Search by name, email, subject, or message"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </label>
        {searchInput ? (
          <button type="button" className="admin-search-clear" onClick={handleClearSearch}>
            Clear
          </button>
        ) : null}
      </div>

      {loading || isSearchPending ? (
        <div className="admin-state">
          <FaSpinner className="admin-spin-icon" aria-hidden="true" />
          <p>Loading feedback…</p>
        </div>
      ) : error ? (
        <div className="admin-state admin-state-error">
          <FaExclamationTriangle aria-hidden="true" />
          <p>{error}</p>
          <button type="button" onClick={fetchFeedback}>Try again</button>
        </div>
      ) : items.length === 0 ? (
        <div className="admin-state">
          <FaClipboardList aria-hidden="true" />
          <p>{search ? `No feedback found for "${search}".` : 'No feedback submissions yet.'}</p>
        </div>
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table admin-feedback-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Category</th>
                  <th>Subject</th>
                  <th>Message</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const listedUser = getFeedbackUser(item);
                  return (
                    <tr key={item.id || `${listedUser.email}-${getFeedbackCreatedAt(item)}`}>
                      <td data-label="User">
                        <div className="admin-user-cell">
                          <span className="admin-user-avatar" aria-hidden="true">
                            {(listedUser.name || listedUser.email || 'U').charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <strong>{listedUser.name}</strong>
                            <span className="admin-subtext">
                              {listedUser.email || (listedUser.id ? `User #${listedUser.id}` : '—')}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td data-label="Category">
                        <span className={`admin-category-badge ${getCategoryClass(item.category)}`}>
                          {getCategoryLabel(item.category)}
                        </span>
                      </td>
                      <td data-label="Subject">{getFeedbackSubject(item)}</td>
                      <td data-label="Message">
                        <span className="admin-feedback-message">{getFeedbackMessage(item) || '—'}</span>
                      </td>
                      <td data-label="Submitted">{formatDateTime(getFeedbackCreatedAt(item))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="admin-cards">
            {items.map((item) => {
              const listedUser = getFeedbackUser(item);
              return (
                <article key={item.id || `${listedUser.email}-${getFeedbackCreatedAt(item)}`} className="admin-card">
                  <div className="admin-card-top">
                    <div className="admin-user-cell">
                      <span className="admin-user-avatar" aria-hidden="true">
                        {(listedUser.name || listedUser.email || 'U').charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <strong>{listedUser.name}</strong>
                        <p className="admin-subtext">{listedUser.email || '—'}</p>
                      </div>
                    </div>
                    <span className={`admin-category-badge ${getCategoryClass(item.category)}`}>
                      {getCategoryLabel(item.category)}
                    </span>
                  </div>
                  <dl>
                    <div>
                      <dt>Subject</dt>
                      <dd>{getFeedbackSubject(item)}</dd>
                    </div>
                    <div>
                      <dt>Submitted</dt>
                      <dd>{formatDateTime(getFeedbackCreatedAt(item))}</dd>
                    </div>
                  </dl>
                  {getFeedbackMessage(item) ? (
                    <p className="admin-feedback-message">{getFeedbackMessage(item)}</p>
                  ) : null}
                </article>
              );
            })}
          </div>

          <div className="admin-pagination" aria-label="Admin feedback pagination">
            <p className="admin-pagination-summary">
              Showing {rangeStart}–{rangeEnd} of {pagination.total} submissions
            </p>
            <div className="admin-pagination-controls">
              <button
                type="button"
                className="admin-pagination-btn"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>
              <div className="admin-pagination-pages">
                {visiblePages.map((pageNumber, index) => {
                  const prevPage = visiblePages[index - 1];
                  const showEllipsis = prevPage && pageNumber - prevPage > 1;
                  return (
                    <React.Fragment key={pageNumber}>
                      {showEllipsis ? <span className="admin-pagination-ellipsis">…</span> : null}
                      <button
                        type="button"
                        className={`admin-pagination-page ${pageNumber === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNumber)}
                        disabled={loading}
                        aria-current={pageNumber === page ? 'page' : undefined}
                      >
                        {pageNumber}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
              <button
                type="button"
                className="admin-pagination-btn"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages || loading || pagination.total === 0}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default AdminFeedbackPanel;
